"use client";

import { useState, useCallback, useEffect } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, Card, CardBody, Image, Chip, Divider, Progress, Accordion, AccordionItem } from "@heroui/react";
import { useSelectedNFTs } from "@/hooks/portfolio/useSelectedNFTs";
import { Icon } from "@iconify/react";
import { isAddress, type Address } from "viem";
import { useConnection, useWriteContract, useWaitForTransactionReceipt, useReadContracts } from "wagmi";
import { erc721BulkTransferAbi } from "@/constants/abi/erc721BulkTransferAbi";
import { type ChainType } from "@/constants/chain-specific";
import { getBulkTransferContract, isBulkTransferSupported } from "@/constants/chain-specific/bulkTransferContracts";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import { erc721Abi } from "viem";
import { defaultIpfsGateway } from "@/config/index";
interface Recipient {
  id: string;
  address: string;
  isValid: boolean;
  nfts: Array<{
    id: string;
    contractAddress: Address;
    tokenId: string;
    collectionName: string;
    image?: string;
    name?: string;
  }>;
}
interface ApprovalStatus {
  contractAddress: Address;
  collectionName: string;
  isApproved: boolean;
  isApproving: boolean;
  approvalHash?: string;
  error?: string;
}
export const BulkTransferModal = () => {
  const {
    selectedNFTs,
    selectedCount,
    bulkTransferModalOpen,
    closeBulkTransferModal,
    clearSelection,
    removeNFTs
  } = useSelectedNFTs();
  const {
    address: userAddress,
    chainId
  } = useConnection();
  const {
    writeContract,
    data: hash,
    isPending: isWritePending
  } = useWriteContract();
  const {
    isLoading: isConfirming,
    isSuccess: isTransferSuccess
  } = useWaitForTransactionReceipt({
    hash
  });

  // Separate state for tracking the last transaction type
  const [lastTransactionType, setLastTransactionType] = useState<"approval" | "transfer" | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([{
    id: "1",
    address: "",
    isValid: false,
    nfts: []
  }]);
  const [unassignedNFTs, setUnassignedNFTs] = useState(selectedNFTs);
  const [transferMode, setTransferMode] = useState<"single" | "multiple">("single");
  const [approvalStatuses, setApprovalStatuses] = useState<ApprovalStatus[]>([]);
  const [currentStep, setCurrentStep] = useState<"setup" | "approvals" | "transfer">("setup");
  const [isApprovingAll, setIsApprovingAll] = useState(false);
  const [pendingApprovalHashes, setPendingApprovalHashes] = useState<Set<string>>(new Set());

  // Get the bulk transfer contract for the current chain
  const bulkTransferContract = chainId ? getBulkTransferContract(chainId) : undefined;
  const isChainSupported = chainId ? isBulkTransferSupported(chainId) : false;

  // Initialize unassigned NFTs when modal opens
  useEffect(() => {
    if (bulkTransferModalOpen) {
      setUnassignedNFTs(selectedNFTs);
    }
  }, [bulkTransferModalOpen, selectedNFTs]);

  // Get unique collections from selected NFTs
  const uniqueCollections = selectedNFTs.reduce((acc, nft) => {
    if (!acc.find(item => item.contractAddress === nft.contractAddress)) {
      acc.push({
        contractAddress: nft.contractAddress,
        collectionName: nft.collectionName
      });
    }
    return acc;
  }, [] as Array<{
    contractAddress: Address;
    collectionName: string;
  }>);

  // Check approval status for all collections - refetch every 3 seconds during approval process
  const {
    data: approvalData,
    refetch: refetchApprovals
  } = useReadContracts({
    contracts: uniqueCollections.map(collection => ({
      address: collection.contractAddress,
      abi: erc721Abi,
      functionName: 'isApprovedForAll',
      args: [userAddress!, bulkTransferContract!],
      chainId: chainId as ChainType
    })),
    query: {
      enabled: !!userAddress && !!bulkTransferContract && uniqueCollections.length > 0,
      refetchInterval: currentStep === "approvals" ? 3000 : false // Refetch every 3 seconds during approval step
    }
  });

  // Update approval statuses when data changes
  useEffect(() => {
    if (!approvalData || uniqueCollections.length === 0) return;
    const newStatuses: ApprovalStatus[] = uniqueCollections.map((collection_0, index) => {
      const existingStatus = approvalStatuses.find(s => s.contractAddress === collection_0.contractAddress);
      const isApproved = Boolean(approvalData[index]?.result);
      const wasApproving = existingStatus?.isApproving || false;
      const justApproved = isApproved && wasApproving;
      return {
        contractAddress: collection_0.contractAddress,
        collectionName: collection_0.collectionName,
        isApproved,
        isApproving: justApproved ? false : existingStatus?.isApproving || false,
        approvalHash: existingStatus?.approvalHash,
        error: justApproved ? undefined : existingStatus?.error
      };
    });

    // Only update if there are actual changes
    const hasChanges = newStatuses.some((newStatus, index_0) => {
      const oldStatus = approvalStatuses[index_0];
      return !oldStatus || oldStatus.isApproved !== newStatus.isApproved || oldStatus.isApproving !== newStatus.isApproving || oldStatus.error !== newStatus.error;
    });
    if (hasChanges) {
      setApprovalStatuses(newStatuses);
    }
  }, [approvalData, uniqueCollections]);

  // Auto-progress to transfer step when all approvals are complete
  useEffect(() => {
    if (currentStep !== "approvals" || approvalStatuses.length === 0) return;
    const allApproved = approvalStatuses.every(status => status.isApproved);
    const noneApproving = !approvalStatuses.some(status_0 => status_0.isApproving);
    if (allApproved && noneApproving && !isApprovingAll) {
      const timer = setTimeout(() => {
        setCurrentStep("transfer");
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [approvalStatuses, currentStep, isApprovingAll]);

  // Handle successful transfer - remove transferred NFTs
  useEffect(() => {
    if (!isTransferSuccess || lastTransactionType !== "transfer") return;
    const transferredNFTs = transferMode === "single" ? selectedNFTs : recipients.flatMap(r => r.nfts);
    removeNFTs(transferredNFTs.map(nft_0 => nft_0.id));
    if (transferMode === "multiple") {
      setRecipients([{
        id: "1",
        address: "",
        isValid: false,
        nfts: []
      }]);
    } else {
      const timer_0 = setTimeout(() => {
        closeBulkTransferModal();
      }, 2000);
      return () => clearTimeout(timer_0);
    }
    setCurrentStep("setup");
    setApprovalStatuses([]);
    setIsApprovingAll(false);
    setPendingApprovalHashes(new Set());
    setLastTransactionType(null);
  }, [isTransferSuccess, lastTransactionType, transferMode, selectedNFTs, recipients, removeNFTs, closeBulkTransferModal]);
  const validateAddress = (address: string): boolean => {
    return isAddress(address);
  };
  const addRecipient = () => {
    const newRecipient: Recipient = {
      id: Date.now().toString(),
      address: "",
      isValid: false,
      nfts: []
    };
    setRecipients(prev => [...prev, newRecipient]);
    setTransferMode("multiple");
  };
  const removeRecipient = (recipientId: string) => {
    setRecipients(prev_0 => {
      const recipientToRemove = prev_0.find(r_0 => r_0.id === recipientId);
      if (recipientToRemove) {
        // Move NFTs back to unassigned
        setUnassignedNFTs(current => [...current, ...recipientToRemove.nfts]);
      }
      const newRecipients = prev_0.filter(r_1 => r_1.id !== recipientId);

      // If only one recipient left, switch to single mode
      if (newRecipients.length === 1) {
        setTransferMode("single");
      }
      return newRecipients;
    });
  };
  const updateRecipientAddress = (recipientId_0: string, address_0: string) => {
    setRecipients(prev_1 => prev_1.map(recipient => recipient.id === recipientId_0 ? {
      ...recipient,
      address: address_0,
      isValid: validateAddress(address_0)
    } : recipient));
  };
  const onDragEnd = useCallback((result: DropResult) => {
    const {
      destination,
      source,
      draggableId
    } = result;
    if (!destination) return;

    // Find the NFT being dragged
    const draggedNFT = [...unassignedNFTs, ...recipients.flatMap(r_2 => r_2.nfts)].find(nft_1 => nft_1.id === draggableId);
    if (!draggedNFT) return;

    // Remove NFT from source
    if (source.droppableId === "unassigned") {
      setUnassignedNFTs(prev_2 => prev_2.filter(nft_2 => nft_2.id !== draggableId));
    } else {
      setRecipients(prev_3 => prev_3.map(recipient_0 => recipient_0.id === source.droppableId ? {
        ...recipient_0,
        nfts: recipient_0.nfts.filter(nft_3 => nft_3.id !== draggableId)
      } : recipient_0));
    }

    // Add NFT to destination
    if (destination.droppableId === "unassigned") {
      setUnassignedNFTs(prev_4 => [...prev_4, draggedNFT]);
    } else {
      setRecipients(prev_5 => prev_5.map(recipient_1 => recipient_1.id === destination.droppableId ? {
        ...recipient_1,
        nfts: [...recipient_1.nfts, draggedNFT]
      } : recipient_1));
    }
  }, [unassignedNFTs, recipients]);
  const handleApproveCollection = async (contractAddress: Address) => {
    if (!bulkTransferContract) return;
    try {
      setApprovalStatuses(prev_7 => prev_7.map(status_2 => status_2.contractAddress === contractAddress ? {
        ...status_2,
        isApproving: true,
        error: undefined
      } : status_2));
      setLastTransactionType("approval");
      await writeContract({
        address: contractAddress,
        abi: erc721Abi,
        functionName: 'setApprovalForAll',
        args: [bulkTransferContract, true]
      });

      // The hash will be available in the `hash` state from useWriteContract
      // We'll track it when the transaction is confirmed
    } catch (error) {
      console.error("Approval failed:", error);
      setLastTransactionType(null);
      setApprovalStatuses(prev_6 => prev_6.map(status_1 => status_1.contractAddress === contractAddress ? {
        ...status_1,
        isApproving: false,
        error: error instanceof Error ? error.message : 'Approval failed'
      } : status_1));
    }
  };
  const handleApproveAll = async () => {
    if (!bulkTransferContract) return;
    const unapprovedCollections = approvalStatuses.filter(status_3 => !status_3.isApproved);
    if (unapprovedCollections.length === 0) return;
    setIsApprovingAll(true);
    try {
      // Set all unapproved collections to approving state
      setApprovalStatuses(prev => prev.map(status => !status.isApproved ? {
        ...status,
        isApproving: true,
        error: undefined
      } : status));

      // Execute approvals sequentially to avoid nonce conflicts
      for (const collection of unapprovedCollections) {
        try {
          setLastTransactionType("approval");
          await writeContract({
            address: collection.contractAddress,
            abi: erc721Abi,
            functionName: 'setApprovalForAll',
            args: [bulkTransferContract, true]
          });

          // Small delay between transactions to avoid issues
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (error) {
          console.error(`Approval failed for ${collection.collectionName}:`, error);
          setLastTransactionType(null);
          setApprovalStatuses(prev => prev.map(status => status.contractAddress === collection.contractAddress ? {
            ...status,
            isApproving: false,
            error: error instanceof Error ? error.message : 'Approval failed'
          } : status));
        }
      }
    } finally {
      setIsApprovingAll(false);
    }
  };
  const handleProceedToApprovals = () => {
    setCurrentStep("approvals");
  };
  const handleProceedToTransfer = () => {
    setCurrentStep("transfer");
  };
  const handleTransfer = async () => {
    if (!chainId || !bulkTransferContract) return;
    try {
      setLastTransactionType("transfer");
      if (transferMode === "single") {
        // Single recipient transfer
        const recipient_2 = recipients[0];
        if (!recipient_2.isValid) return;

        // Group NFTs by contract
        const contractGroups = selectedNFTs.reduce((acc_0, nft_4) => {
          if (!acc_0[nft_4.contractAddress]) {
            acc_0[nft_4.contractAddress] = [];
          }
          acc_0[nft_4.contractAddress].push(BigInt(nft_4.tokenId));
          return acc_0;
        }, {} as Record<string, bigint[]>);
        const contracts = Object.keys(contractGroups) as Address[];
        const tokenIds = Object.values(contractGroups);
        await writeContract({
          address: bulkTransferContract,
          abi: erc721BulkTransferAbi,
          functionName: "safeBatchTransferToSingleWallet",
          args: [contracts, recipient_2.address as Address, tokenIds]
        });
      } else {
        // Multiple recipients transfer - use structured approach
        const validRecipients = recipients.filter(r_3 => r_3.isValid && r_3.nfts.length > 0);

        // Group by contract
        const contractGroups_0: Record<string, {
          tokenIds: bigint[];
          recipients: Address[];
        }> = {};
        validRecipients.forEach(recipient_3 => {
          recipient_3.nfts.forEach(nft_5 => {
            if (!contractGroups_0[nft_5.contractAddress]) {
              contractGroups_0[nft_5.contractAddress] = {
                tokenIds: [],
                recipients: []
              };
            }
            contractGroups_0[nft_5.contractAddress].tokenIds.push(BigInt(nft_5.tokenId));
            contractGroups_0[nft_5.contractAddress].recipients.push(recipient_3.address as Address);
          });
        });
        const contracts_0 = Object.keys(contractGroups_0) as Address[];
        const tokenIds_0 = Object.values(contractGroups_0).map(group => group.tokenIds);
        const recipientArrays = Object.values(contractGroups_0).map(group_0 => group_0.recipients);
        await writeContract({
          address: bulkTransferContract,
          abi: erc721BulkTransferAbi,
          functionName: "safeBatchTransferToMultipleWalletsStructured",
          args: [contracts_0, recipientArrays, tokenIds_0]
        });
      }
    } catch (error_0) {
      console.error("Transfer failed:", error_0);
      setLastTransactionType(null);
    }
  };
  const handleClose = () => {
    closeBulkTransferModal();
    // Reset state
    setRecipients([{
      id: "1",
      address: "",
      isValid: false,
      nfts: []
    }]);
    setTransferMode("single");
    setCurrentStep("setup");
    setApprovalStatuses([]);
    setIsApprovingAll(false);
    setPendingApprovalHashes(new Set());
  };
  const allApproved_0 = approvalStatuses.length > 0 && approvalStatuses.every(status_4 => status_4.isApproved);
  const hasUnapprovedCollections = approvalStatuses.some(status_5 => !status_5.isApproved);
  const anyApproving = approvalStatuses.some(status_6 => status_6.isApproving);
  const canProceedToApprovals = isChainSupported && bulkTransferContract && (transferMode === "single" ? recipients[0]?.isValid && selectedCount > 0 : recipients.some(r_4 => r_4.isValid && r_4.nfts.length > 0));
  const canTransfer = allApproved_0 && canProceedToApprovals;
  return <Modal isOpen={bulkTransferModalOpen} onClose={handleClose} size="4xl" scrollBehavior="inside" classNames={{
    base: "max-h-[90vh]",
    body: "py-6"
  }}>
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold">Bulk Transfer NFTs</h3>
            <div className="flex items-center gap-2">
              <Chip color="primary" variant="flat">
                {selectedCount} NFT{selectedCount !== 1 ? 's' : ''} selected
              </Chip>
              {currentStep === "approvals" && <Chip color={allApproved_0 ? "success" : "warning"} variant="flat">
                  {allApproved_0 ? "✓ Approvals Complete" : "Step 1: Approvals"}
                </Chip>}
              {currentStep === "transfer" && <Chip color="success" variant="flat">
                  Step 2: Transfer
                </Chip>}
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          {/* Chain Support Warning */}
          {!isChainSupported && <div className="mb-4 p-4 bg-warning-50 border border-warning-200 rounded-lg">
              <div className="flex items-center gap-2 text-warning-700">
                <Icon icon="mdi:alert-circle" />
                <span>Bulk transfer is not supported on this chain yet.</span>
              </div>
            </div>}

          {isConfirming && currentStep === "transfer" && <div className="mb-4">
              <Progress label="Confirming transfer transaction..." value={50} className="max-w-md" color="primary" isIndeterminate />
            </div>}

          {isTransferSuccess && <div className="mb-4 p-4 bg-success-50 border border-success-200 rounded-lg">
              <div className="flex items-center gap-2 text-success-700">
                <Icon icon="mdi:check-circle" />
                <span>
                  Transfer completed successfully!
                  {transferMode === "multiple" && unassignedNFTs.length > 0 && <span className="block text-sm mt-1">
                      {unassignedNFTs.length} NFT{unassignedNFTs.length !== 1 ? 's' : ''} remaining for additional transfers.
                    </span>}
                </span>
              </div>
            </div>}

          {/* Approval Step */}
          {currentStep === "approvals" && isChainSupported && <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 text-blue-700 mb-2">
                  <Icon icon="mdi:shield-check" />
                  <span className="font-medium">Collection Approvals Required</span>
                </div>
                <p className="text-sm text-blue-600">
                  Before transferring NFTs, you need to approve the bulk transfer contract to manage your NFTs from each collection.
                </p>
              </div>

              {/* Progress indicator */}
              {approvalStatuses.length > 0 && <div className="p-3 bg-default-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Approval Progress</span>
                    <span className="text-sm text-default-600">
                      {approvalStatuses.filter(s_0 => s_0.isApproved).length} / {approvalStatuses.length} approved
                    </span>
                  </div>
                  <Progress value={approvalStatuses.filter(s_1 => s_1.isApproved).length / approvalStatuses.length * 100} color={allApproved_0 ? "success" : "primary"} className="w-full" />
                </div>}

              <div className="space-y-3">
                {approvalStatuses.map(status_7 => <Card key={status_7.contractAddress} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${status_7.isApproved ? 'bg-success' : status_7.isApproving ? 'bg-warning animate-pulse' : 'bg-default-300'}`} />
                        <div>
                          <div className="font-medium">{status_7.collectionName}</div>
                          <div className="text-xs text-default-500 font-thin">
                            {status_7.contractAddress}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {status_7.isApproved ? <Chip color="success" size="sm" startContent={<Icon icon="mdi:check" />}>
                            Approved
                          </Chip> : status_7.isApproving ? <Chip color="warning" size="sm" startContent={<Icon icon="mdi:loading" className="mr-1 animate-spin" />}>
                            Confirming...
                          </Chip> : <Button size="sm" color="primary" onPress={() => handleApproveCollection(status_7.contractAddress)} startContent={<Icon icon="mdi:shield-check" />} isDisabled={isApprovingAll}>
                            Approve
                          </Button>}
                      </div>
                    </div>

                    {status_7.error && <div className="mt-2 text-sm text-danger">
                        Error: {status_7.error}
                      </div>}
                  </Card>)}
              </div>

              {/* Approve All Button */}
              {hasUnapprovedCollections && <div className="flex justify-center pt-4 border-t border-default-200">
                  <Button color="primary" size="lg" onPress={handleApproveAll} isLoading={isApprovingAll} isDisabled={anyApproving} startContent={<Icon icon="mdi:shield-check-outline" />}>
                    {isApprovingAll ? "Approving All Collections..." : `Approve All ${approvalStatuses.filter(s_2 => !s_2.isApproved).length} Collections`}
                  </Button>
                </div>}

              {/* Auto-progress message */}
              {allApproved_0 && !anyApproving && <div className="p-4 bg-success-50 border border-success-200 rounded-lg">
                  <div className="flex items-center gap-2 text-success-700">
                    <Icon icon="mdi:check-circle" />
                    <span>All collections approved! Proceeding to transfer step...</span>
                  </div>
                </div>}
            </div>}

          {/* Setup and Transfer Steps */}
          {(currentStep === "setup" || currentStep === "transfer") && isChainSupported && <DragDropContext onDragEnd={onDragEnd}>
              <div className="space-y-6">
                {/* Recipients Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-medium">Recipients</h4>
                    <Button size="sm" variant="flat" startContent={<Icon icon="mdi:plus" />} onPress={addRecipient}>
                      Add Recipient
                    </Button>
                  </div>

                  {recipients.map((recipient_4, index_1) => <Card key={recipient_4.id} className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <Input placeholder="Enter wallet address (0x...)" value={recipient_4.address} onValueChange={value => updateRecipientAddress(recipient_4.id, value)} isInvalid={recipient_4.address.length > 0 && !recipient_4.isValid} errorMessage={recipient_4.address.length > 0 && !recipient_4.isValid ? "Invalid wallet address" : undefined} startContent={<Icon icon="mdi:wallet" className="text-default-400" />} className="flex-1" />

                        {recipients.length > 1 && <Button isIconOnly size="sm" variant="flat" color="danger" onPress={() => removeRecipient(recipient_4.id)}>
                            <Icon icon="mdi:trash" />
                          </Button>}
                      </div>

                      {/* NFT Drop Zone for Multiple Recipients */}
                      {transferMode === "multiple" && <Droppable droppableId={recipient_4.id}>
                          {(provided, snapshot) => <div ref={provided.innerRef} {...provided.droppableProps} className={`min-h-[100px] p-3 border-2 border-dashed rounded-lg transition-colors ${snapshot.isDraggingOver ? 'border-primary bg-primary-50' : 'border-default-300'}`}>
                              {recipient_4.nfts.length === 0 ? <div className="flex items-center justify-center h-full text-default-400">
                                  <div className="text-center">
                                    <Icon icon="mdi:drag" className="text-2xl mb-2" />
                                    <div className="text-sm">Drag NFTs here</div>
                                  </div>
                                </div> : <div className="grid grid-cols-4 gap-2">
                                  {recipient_4.nfts.map((nft_6, nftIndex) => <Draggable key={nft_6.id} draggableId={nft_6.id} index={nftIndex}>
                                      {provided_0 => <div ref={provided_0.innerRef} {...provided_0.draggableProps} {...provided_0.dragHandleProps} className="cursor-move">
                                          <Card className="p-2">
                                            <div className="aspect-square relative mb-2">
                                              {nft_6.image ? <Image src={nft_6.image.replace('ipfs://', defaultIpfsGateway)} alt={nft_6.name || `Token #${nft_6.tokenId}`} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-default-100 flex items-center justify-center">
                                                  <Icon icon="mdi:image-off" className="text-default-400" />
                                                </div>}
                                            </div>
                                            <div className="text-xs text-center">
                                              <div className="font-medium truncate">
                                                {nft_6.collectionName}
                                              </div>
                                              <div className="text-default-500">#{nft_6.tokenId}</div>
                                            </div>
                                          </Card>
                                        </div>}
                                    </Draggable>)}
                                </div>}
                              {provided.placeholder}
                            </div>}
                        </Droppable>}
                    </Card>)}
                </div>

                {/* Selected NFTs Section */}
                {transferMode === "single" ? <div>
                    <h4 className="text-lg font-medium mb-3">Selected NFTs</h4>
                    <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                      {selectedNFTs.map(nft_7 => <Card key={nft_7.id} className="p-2">
                          <div className="aspect-square relative mb-2">
                            {nft_7.image ? <Image src={nft_7.image.replace('ipfs://', defaultIpfsGateway)} alt={nft_7.name || `Token #${nft_7.tokenId}`} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-default-100 flex items-center justify-center">
                                <Icon icon="mdi:image-off" className="text-default-400" />
                              </div>}
                          </div>
                          <div className="text-xs text-center">
                            <div className="font-medium truncate">{nft_7.collectionName}</div>
                            <div className="text-default-500">#{nft_7.tokenId}</div>
                          </div>
                        </Card>)}
                    </div>
                  </div> : <div>
                    <h4 className="text-lg font-medium mb-3">Unassigned NFTs</h4>
                    <Droppable droppableId="unassigned">
                      {(provided_1, snapshot_0) => <div ref={provided_1.innerRef} {...provided_1.droppableProps} className={`min-h-[120px] p-4 border-2 border-dashed rounded-lg transition-colors ${snapshot_0.isDraggingOver ? 'border-warning bg-warning-50' : 'border-default-300'}`}>
                          {unassignedNFTs.length === 0 ? <div className="flex items-center justify-center h-full text-default-400">
                              <div className="text-center">
                                <Icon icon="mdi:check-circle" className="text-2xl mb-2" />
                                <div className="text-sm">All NFTs assigned</div>
                              </div>
                            </div> : <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                              {unassignedNFTs.map((nft_8, index_2) => <Draggable key={nft_8.id} draggableId={nft_8.id} index={index_2}>
                                  {provided_2 => <div ref={provided_2.innerRef} {...provided_2.draggableProps} {...provided_2.dragHandleProps} className="cursor-move">
                                      <Card className="p-2">
                                        <div className="aspect-square relative mb-2">
                                          {nft_8.image ? <Image src={nft_8.image.replace('ipfs://', defaultIpfsGateway)} alt={nft_8.name || `Token #${nft_8.tokenId}`} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-default-100 flex items-center justify-center">
                                              <Icon icon="mdi:image-off" className="text-default-400" />
                                            </div>}
                                        </div>
                                        <div className="text-xs text-center">
                                          <div className="font-medium truncate">{nft_8.collectionName}</div>
                                          <div className="text-default-500">#{nft_8.tokenId}</div>
                                        </div>
                                      </Card>
                                    </div>}
                                </Draggable>)}
                            </div>}
                          {provided_1.placeholder}
                        </div>}
                    </Droppable>
                  </div>}
              </div>
            </DragDropContext>}

          {/* Contract Info */}
          {isChainSupported && bulkTransferContract && <div className="mt-4 p-3 bg-default-50 rounded-lg">
              <div className="text-xs text-default-600">
                <div className="font-medium mb-1">Bulk Transfer Contract:</div>
                <div className="font-thin text-xs break-all">{bulkTransferContract}</div>
              </div>
            </div>}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={handleClose}>
            Cancel
          </Button>

          {currentStep === "setup" && <Button color="primary" onPress={handleProceedToApprovals} isDisabled={!canProceedToApprovals}>
              Next: Check Approvals
            </Button>}

          {currentStep === "approvals" && !allApproved_0 && <Button color="primary" onPress={handleProceedToTransfer} isDisabled={!allApproved_0}>
              Waiting for Approvals...
            </Button>}

          {currentStep === "transfer" && <Button color="success" onPress={handleTransfer} isDisabled={!canTransfer || isWritePending || isConfirming} isLoading={isWritePending || isConfirming}>
              {isWritePending ? "Preparing Transfer..." : isConfirming ? "Confirming Transfer..." : "Execute Transfer"}
            </Button>}
        </ModalFooter>
      </ModalContent>
    </Modal>;
};